<div class="label-data-review-title">
  <h1 class="title">Create Product Labels</h1>
  @if (isLoading) {
    <mat-spinner class="spinner"></mat-spinner>
  }
</div>

@if (!isLoading && !showSkipConfirmation) {
  <mat-dialog-content class="label-data-review-content">
    <div class="validation-summary">
      <mat-icon [class.status-green]="allComplete" [class.status-red]="!allComplete">
        {{ allComplete ? 'check_circle' : 'error' }}
      </mat-icon>
      <span class="summary-text" [class.status-green]="allComplete" [class.status-red]="!allComplete">
        {{ productsWithLabels }}/{{ productStatuses.length }} products have asset labels
      </span>
      <span class="spacer"></span>
      <button mat-icon-button matTooltip="Refresh" (click)="refreshStatus()">
        <mat-icon>refresh</mat-icon>
      </button>
      <button mat-stroked-button class="collapse-btn" (click)="anyExpanded ? collapseAll() : expandAll()">
        {{ anyExpanded ? 'Collapse All' : 'Expand All' }}
      </button>
    </div>
    <div class="product-list">
      @for (product of productStatuses; track product) {
        <div class="product-row">
          <div class="product-header clickable"
            [class.highlighted]="isProductHighlighted(product.productId)"
            (click)="toggleProduct(product.productId)">
            <mat-icon class="expand-icon">
              {{ isProductExpanded(product.productId) ? 'expand_more' : 'chevron_right' }}
            </mat-icon>
            <mat-icon class="status-icon"
              [class.status-green]="productStatus(product) === 'green'"
              [class.status-orange]="productStatus(product) === 'orange'"
              [class.status-red]="productStatus(product) === 'red'">
              {{ productStatus(product) === 'green' ? 'check_circle' : productStatus(product) === 'orange' ? 'info' : 'warning' }}
            </mat-icon>
            <span class="product-name"
              [class.status-green]="productStatus(product) === 'green'"
              [class.status-orange]="productStatus(product) === 'orange'"
              [class.status-red]="productStatus(product) === 'red'">
              {{ product.barcode || product.productId }}
            </span>
            @if (productStatus(product) === 'orange') {
              <span class="no-label-text">Asset labels created, no content</span>
            }
            @if (productStatus(product) === 'red') {
              <span class="no-label-text">No asset labels</span>
            }
            <span class="label-count">{{ labeledCount(product) }}/{{ product.postAssetCount }}</span>
            <button mat-stroked-button class="collapse-btn"
              (click)="toggleProductAssets(product); $event.stopPropagation()">
              {{ anyAssetsExpanded(product) ? 'Collapse All' : 'Expand All' }}
            </button>
          </div>
          @if (isProductExpanded(product.productId)) {
            <div class="asset-list">
              @for (asset of product.assetLabels; track asset; let i = $index) {
                <div class="asset-row"
                  [class.highlighted]="isHighlighted(asset.postAssetId)">
                  <div class="asset-header"
                    [class.clickable]="hasValidLabel(asset)"
                    (click)="hasValidLabel(asset) ? toggleAsset(asset.postAssetId) : null">
                    <mat-icon class="expand-icon" [class.placeholder-icon]="!hasValidLabel(asset)">
                      {{ !hasValidLabel(asset) ? 'remove' : isAssetExpanded(asset.postAssetId) ? 'expand_more' : 'chevron_right' }}
                    </mat-icon>
                    <mat-icon class="asset-status-icon"
                      [class.has-json]="hasValidLabel(asset)"
                      [class.status-red]="!hasValidLabel(asset)">
                      {{ !asset.hasLabel ? 'remove_circle_outline' : 'data_object' }}
                    </mat-icon>
                    <span class="asset-id"
                      [class.has-json]="hasValidLabel(asset)"
                      [class.status-red]="!hasValidLabel(asset)">
                      {{ i + 1 }}
                    </span>
                    @if (asset.postAssetId) {
                      <a [href]="getPostAssetUrl(product, asset)" target="_blank"
                        class="asset-link" (click)="$event.stopPropagation()">
                        <mat-icon>open_in_new</mat-icon>
                      </a>
                    }
                    @if (!asset.postAssetId) {
                      <mat-icon class="asset-link disabled">open_in_new</mat-icon>
                    }
                    @if (!asset.postAssetId) {
                      <span class="no-label-text">No post asset</span>
                    }
                    @if (asset.postAssetId && !asset.hasLabel) {
                      <span class="no-label-text">No label data</span>
                    }
                    @if (asset.postAssetId) {
                      <app-label-data-asset-create class="regenerate-btn"
                        [assetId]="asset.postAssetId" [assetType]="'post-asset'"
                        (extracted)="onAssetExtracted(asset.postAssetId)">
                      </app-label-data-asset-create>
                    }
                  </div>
                  @if (isAssetExpanded(asset.postAssetId) && asset.label) {
                    <div class="label-json-preview">
                      <pre>{{ asset.label | json }}</pre>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  </mat-dialog-content>
}

@if (showSkipConfirmation) {
  <mat-dialog-content class="label-data-review-content">
    <div class="skip-confirmation">
      <div class="skip-summary">
        <mat-icon class="status-orange">warning</mat-icon>
        <span class="summary-text">{{ skippedProducts.length }} product(s) will be skipped (no asset labels)</span>
      </div>
      <div class="skip-list">
        @for (product of skippedProducts; track product) {
          <div class="skip-item">
            <mat-icon class="status-red">remove_circle_outline</mat-icon>
            <span>{{ product.barcode || product.productId }}</span>
          </div>
        }
      </div>
      <div class="process-summary">
        <mat-icon class="status-green">check_circle</mat-icon>
        <span class="summary-text">{{ processableProducts.length }} product(s) will be processed</span>
      </div>
    </div>
  </mat-dialog-content>
}

@if (!showSkipConfirmation) {
  <mat-dialog-actions align="end" class="action">
    <button mat-stroked-button [mat-dialog-close]="DialogResult.Cancel">Cancel</button>
    <button
      mat-stroked-button
      color="buttonSuccess"
      class="save"
      [disabled]="isLoading || !someHaveLabels"
      (click)="onGenerate()"
      >
      Generate
    </button>
  </mat-dialog-actions>
}

@if (showSkipConfirmation) {
  <mat-dialog-actions align="end" class="action">
    <button mat-stroked-button (click)="cancelConfirmation()">Back</button>
    <button mat-stroked-button color="buttonSuccess" (click)="confirmGenerate()">
      Proceed
    </button>
  </mat-dialog-actions>
}
